import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Plus,
  Settings,
  Edit,
  Trash2,
  Zap,
  MapPin,
  Battery,
  Timer,
  AlertTriangle,
  Mail,
  Webhook
} from 'lucide-react';

interface AlertRule {
  id: string;
  name: string;
  type: string;
  threshold?: number | null;
  active: boolean;
  geofence_name?: string | null;
  notify_channels: {
    email?: string[];
    webhook?: string[];
  };
  created_at: string;
}

interface AlertRulesManagerProps {
  rules: AlertRule[];
  onCreateRule: (rule: Omit<AlertRule, 'id' | 'created_at'>) => void;
  onUpdateRule: (ruleId: string, rule: Partial<AlertRule>) => void;
  onDeleteRule: (ruleId: string) => void;
}

export function AlertRulesManager({ 
  rules, 
  onCreateRule, 
  onUpdateRule, 
  onDeleteRule 
}: AlertRulesManagerProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    threshold: '',
    geofence_name: '',
    notify_emails: '',
    notify_webhooks: '',
    active: true
  });

  const alertTypes = [
    { value: 'overspeed', label: 'Excesso de Velocidade', icon: Zap, requiresThreshold: true },
    { value: 'enter', label: 'Entrada em Área', icon: MapPin, requiresGeofence: true },
    { value: 'exit', label: 'Saída de Área', icon: MapPin, requiresGeofence: true },
    { value: 'battery_low', label: 'Bateria Baixa', icon: Battery, requiresThreshold: true },
    { value: 'idle', label: 'Tempo Ocioso', icon: Timer, requiresThreshold: true },
    { value: 'power_loss', label: 'Perda de Energia', icon: AlertTriangle }
  ];

  const getAlertTypeInfo = (type: string) => {
    return alertTypes.find(t => t.value === type) || alertTypes[0];
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      threshold: '',
      geofence_name: '',
      notify_emails: '',
      notify_webhooks: '',
      active: true
    });
    setEditingRule(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const ruleData = {
      name: formData.name,
      type: formData.type,
      threshold: formData.threshold ? parseFloat(formData.threshold) : null,
      geofence_name: formData.geofence_name || null,
      notify_channels: {
        email: formData.notify_emails ? formData.notify_emails.split(',').map(e => e.trim()) : [],
        webhook: formData.notify_webhooks ? formData.notify_webhooks.split(',').map(w => w.trim()) : []
      },
      active: formData.active
    };

    if (editingRule) {
      onUpdateRule(editingRule.id, ruleData);
    } else {
      onCreateRule(ruleData);
    }

    resetForm();
    setIsCreateDialogOpen(false);
  };

  const handleEdit = (rule: AlertRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      type: rule.type,
      threshold: rule.threshold?.toString() || '',
      geofence_name: rule.geofence_name || '',
      notify_emails: rule.notify_channels.email?.join(', ') || '',
      notify_webhooks: rule.notify_channels.webhook?.join(', ') || '',
      active: rule.active
    });
    setIsCreateDialogOpen(true);
  };

  const selectedTypeInfo = getAlertTypeInfo(formData.type);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Regras de Alerta</h3>
          <p className="text-sm text-muted-foreground">
            Configure condições para gerar alertas automáticos
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Regra
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? 'Editar Regra' : 'Nova Regra de Alerta'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome da Regra</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Excesso de velocidade na Marginal"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Tipo de Alerta</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {alertTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedTypeInfo?.requiresThreshold && (
                <div className="space-y-2">
                  <Label htmlFor="threshold">
                    Limite {formData.type === 'overspeed' ? '(km/h)' : 
                            formData.type === 'battery_low' ? '(%)' : 
                            formData.type === 'idle' ? '(minutos)' : ''}
                  </Label>
                  <Input
                    id="threshold"
                    type="number"
                    value={formData.threshold}
                    onChange={(e) => setFormData({ ...formData, threshold: e.target.value })}
                    placeholder={formData.type === 'overspeed' ? '80' :
                               formData.type === 'battery_low' ? '20' :
                               formData.type === 'idle' ? '30' : ''}
                    required
                  />
                </div>
              )}

              {selectedTypeInfo?.requiresGeofence && (
                <div className="space-y-2">
                  <Label htmlFor="geofence">Geocerca</Label>
                  <Select 
                    value={formData.geofence_name} 
                    onValueChange={(value) => setFormData({ ...formData, geofence_name: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma geocerca" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centro-sp">Centro de São Paulo</SelectItem>
                      <SelectItem value="marginal-pinheiros">Marginal Pinheiros</SelectItem>
                      <SelectItem value="zona-restrita">Zona Restrita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="emails">E-mails para Notificação</Label>
                <Input
                  id="emails"
                  value={formData.notify_emails}
                  onChange={(e) => setFormData({ ...formData, notify_emails: e.target.value })}
                  placeholder="email1@empresa.com, email2@empresa.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhooks">URLs de Webhook</Label>
                <Input
                  id="webhooks"
                  value={formData.notify_webhooks}
                  onChange={(e) => setFormData({ ...formData, notify_webhooks: e.target.value })}
                  placeholder="https://webhook.site/..."
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Regra ativa</Label>
              </div>

              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsCreateDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingRule ? 'Salvar Alterações' : 'Criar Regra'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rules List */}
      <div className="grid gap-4">
        {rules.map((rule) => {
          const typeInfo = getAlertTypeInfo(rule.type);
          return (
            <Card key={rule.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <typeInfo.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{rule.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {typeInfo.label}
                        {rule.threshold && ` - Limite: ${rule.threshold}${
                          rule.type === 'overspeed' ? ' km/h' :
                          rule.type === 'battery_low' ? '%' :
                          rule.type === 'idle' ? ' min' : ''
                        }`}
                        {rule.geofence_name && ` - Área: ${rule.geofence_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={rule.active ? 'default' : 'secondary'}>
                      {rule.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEdit(rule)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onDeleteRule(rule.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rule.notify_channels.email && rule.notify_channels.email.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {rule.notify_channels.email.length} e-mail(s)
                      </span>
                    </div>
                  )}
                  {rule.notify_channels.webhook && rule.notify_channels.webhook.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Webhook className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {rule.notify_channels.webhook.length} webhook(s)
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {rules.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma regra configurada</h3>
              <p className="text-muted-foreground mb-4">
                Crie regras de alerta para monitorar eventos da sua frota automaticamente
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeira Regra
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}